import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Section } from '../trackvision/shared/model/section.model';
import { Header } from '../trackvision/shared/model/header.model';
import { BackendSignature } from '../trackvision/shared/model/sign.model';
import { Image } from '../trackvision/shared/model/image.model';

interface DocumentContent {
  contentheader: Header,
  contentbody: Section[][],
  contentfooter: {
    signatures: BackendSignature[],
    style: any
  }
  subjectid: number,
  typeid: number,
  requirementtypeid: number,
  description: string,
  valuecustomfield?: string
  dynamicfield?: Array<any>
}
interface Document extends DocumentContent {
  name: string
}

interface DocumentChange extends DocumentContent {
  id: number
}

@Injectable({
  providedIn: 'root'
})
export class DocumentsService {

  unsavedChanges : any []

  constructor(
    private http: HttpClient
  ) { }

  getRequirementsCustomfields() {
    return this.http.get(`${environment.apiUrl}/documents/requirementfields`, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '')
    })
  }

  getTemplatesTypes() {
    return this.http.get(`${environment.apiUrl}/documents/type`, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '')
    })
  }

  getDocumentSections() {
    return this.http.get(`${environment.apiUrl}/documents/section`, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '')
    })
  }

  sendSignatureEmail(body: {
    documentId: number
    subject: string,
    email: string,
    content: string,
    attachments: { path: string | ArrayBuffer }[],
    signatures: BackendSignature[],
    signatureIndex: number
  }) {
    return this.http.post(`${environment.apiUrl}/documents/signature/sendEmail`, body, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  getDocumentForSignature(id: number, token: string) {
    return this.http.get(`${environment.apiUrl}/documents/signature/${id}/sign/${token}`)
  }

  uploadThirdPartySignature(image: Image, token: string, documentId: number) {
    const formData: FormData = new FormData()
    formData.append('file', image.file, image.name)
    formData.append('token', String(token))
    formData.append('documentId', String(documentId))

    return this.http.post(`${environment.apiUrl}/documents/signature/saveThirdParty`, formData)
  }

  uploadImage(image: Image) {
    const formData: FormData = new FormData()
    formData.append('file', image.file, image.name)

    return this.http.post(`${environment.apiUrl}/documents/uploadImage`, formData, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  uploadSignature(image: Image) {
    const formData: FormData = new FormData()
    formData.append('file', image.file, image.name)

    return this.http.post(`${environment.apiUrl}/documents/signature/createSignedSignature`, formData, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  getImageUrl(internalName: string) {
    return this.http.get(`${environment.apiUrl}/documents/getImage/${internalName}`, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '')
    })
  }

  createSection(section: Section) {
    return this.http.post(`${environment.apiUrl}/documents/section/create`, section, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  updateSection(section: Section) {
    return this.http.post(`${environment.apiUrl}/documents/section/update`, section, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  getDocument(id: number) {
    return this.http.get(`${environment.apiUrl}/documents/get/${id}`, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '')
    })
  }

  createDocument(document: Document) {
    return this.http.post(`${environment.apiUrl}/documents/create`, document, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  updateDocument(changes: DocumentChange) {
    const { id, ...body } = changes
    return this.http.post(`${environment.apiUrl}/documents/update/${id}`, body, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  updateDocumentName(id: number, name: string) {
    return this.http.post(`${environment.apiUrl}/documents/updateName/${id}`, { name }, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  getTemplates(subjectId: number = undefined, subjectParentId: number = undefined) {
    const query: any = {}
    if (subjectId)
      query.subjectId = String(subjectId)
    if (subjectParentId)
      query.subjectParentId = String(subjectParentId)

    return this.http.get(`${environment.apiUrl}/documents`, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || ''),
      params: query
    })
  }

  getSubjects(parentId: string = null) {
    return this.http.get(`${environment.apiUrl}/documents/subject`, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || ''),
      params: parentId ? { parentId } : undefined
    })
  }

  getDocumentVersionList(documentId: number) {
    return this.http.get(`${environment.apiUrl}/documents/versions/list/${documentId}`, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '')
    })
  }

  createSubject(subject: {parentid?: number, name: string}) {
    return this.http.post(`${environment.apiUrl}/documents/subject/create`, subject, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  createType(name: string) {
    return this.http.post(`${environment.apiUrl}/documents/type/create`, { name }, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  updateType(id: number, name: string) {
    return this.http.post(`${environment.apiUrl}/documents/type/update/${id}`, { name }, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  grantDocumentPermissions(id: number, emails: string[]) {
    return this.http.post(`${environment.apiUrl}/documents/permissions/grantPermissions/${id}`, { emails }, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  getUsersWithAccess(id: number) {
    return this.http.get(`${environment.apiUrl}/documents/permissions/users/${id}`, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  deleteType(id: number) {
    return this.http.post(`${environment.apiUrl}/documents/type/delete`, { id }, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  updateSubject(id: number, content: {name: string, parentid?: number}) {
    if (!content.parentid)
      delete content.parentid
    return this.http.post(`${environment.apiUrl}/documents/subject/update/${id}`, content, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  deleteSubject(id: number) {
    return this.http.post(`${environment.apiUrl}/documents/subject/delete`, { id }, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  deleteTemplate(id: number) {
    return this.http.post(`${environment.apiUrl}/documents/delete`, { id }, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }

  deleteSection(id: number) {
    return this.http.post(`${environment.apiUrl}/documents/section/delete`, { id }, {
      headers: new HttpHeaders().set('Token', localStorage.getItem('access_token') || '') 
    })
  }
}
